import { useCallback } from "react";

import { TApiButtonCommandFn } from ".";

import { fetchDynamicForm } from "store/actions/flow";
import { useAppDispatch, useAppSelector } from "store/index";

import { find, findIndex } from "lodash-es";

/**
 * @function useRefreshForm
 * @description Creates a command function that refreshes a form in the form stack.
 * @returns {TApiButtonCommandFn} A function that can be used as a button command to refresh forms.
 */
export const useRefreshForm = () => {
  const dispatch = useAppDispatch();
  const flowSessionId = useAppSelector(state => state.formFlow.flowSessionId);
  const formStack = useAppSelector(state => state.formFlow.nextForm);
  const formIndex = useAppSelector(state => state.formFlow.formIndex);

  const refreshFormCommand: TApiButtonCommandFn = useCallback(
    async commandArgs => {
      const targetFormArgument = find(commandArgs, ["name", "targetForm"]);
      const targetFormSlug = targetFormArgument?.source;

      const formToRefreshArg = (function () {
        if (targetFormSlug) {
          /**
           * If targetFormSlug is "previous", select the form before the current one
           * Otherwise, find the form by its slug
           */
          const foundFormIndex = (() => {
            if (targetFormSlug === "previous") {
              return formIndex - 2;
            }
            return findIndex(formStack, form => {
              return "slug" in form && form.slug === targetFormSlug;
            });
          })();

          /**
           * If a matching form is found, extract it and its arguments
           */
          if (foundFormIndex >= 0) {
            return formStack[foundFormIndex].arg;
          }
        }

        /**
         * Fallback: If target form is not found or not specified,
         * use the current form at the top of the stack
         */
        const currentForm = formStack[formIndex - 1];
        return currentForm.arg;
      })();

      if (formToRefreshArg) {
        return await dispatch(
          fetchDynamicForm({
            ...formToRefreshArg,
            flowSessionId,
          })
        ).unwrap();
      }
    },
    [dispatch, flowSessionId, formStack, formIndex]
  );

  return refreshFormCommand;
};
